/**
 * Session Manager Component - Handles session timeouts and security events
 * Provides session warnings, automatic logout, and activity monitoring
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  Box,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Icon,
  ProgressBar,
} from '@shopify/polaris';
import {
  AlertTriangleIcon,
  ClockIcon,
  ExitIcon,
  RefreshIcon,
} from '@shopify/polaris-icons';
import { secureAuth, secureAuthUtils } from '../utils/secureAuth';

export default function SessionManager({ onLogout, onSessionExtended }) {
  const [sessionWarning, setSessionWarning] = useState(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [authFailure, setAuthFailure] = useState(null);
  const [countdownTimer, setCountdownTimer] = useState(0);
  
  // Format time remaining for display
  const formatTimeRemaining = useCallback((milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Handle session warning
  const handleSessionWarning = useCallback((event) => {
    const { timeLeft } = event.detail || {};
    setSessionWarning({ timeLeft });
    setCountdownTimer(timeLeft);
  }, []);

  // Handle automatic logout
  const handleSessionLogout = useCallback((event) => {
    const { reason } = event.detail || { reason: 'session_expired' };
    setIsSessionExpired(true);
    setSessionWarning(null);
    
    // Clear countdown timer
    setCountdownTimer(0);
    
    if (onLogout) {
      setTimeout(() => onLogout(reason), 1000);
    }
  }, [onLogout]);

  // Handle authentication failures
  const handleAuthFailure = useCallback((event) => {
    const { reason, endpoint } = event.detail || {};
    setAuthFailure({ reason, endpoint });
    
    // Auto-logout after showing error
    setTimeout(() => {
      setAuthFailure(null);
      if (onLogout) {
        onLogout('auth_failure');
      }
    }, 3000);
  }, [onLogout]);

  // Handle session restoration
  const handleSessionRestored = useCallback((event) => {
    const { userData } = event.detail || {};
    // Session restored
  }, []);

  // Extend session
  const handleExtendSession = useCallback(async () => {
    try {
      // Update activity to extend session
      secureAuthUtils.updateActivity();
      
      // Clear warning
      setSessionWarning(null);
      setCountdownTimer(0);
      
      if (onSessionExtended) {
        onSessionExtended();
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
    }
  }, [onSessionExtended]);

  // Force logout
  const handleForceLogout = useCallback(() => {
    setSessionWarning(null);
    setCountdownTimer(0);
    
    if (onLogout) {
      onLogout('user_requested');
    }
  }, [onLogout]);

  // Countdown timer for session warning
  useEffect(() => {
    if (sessionWarning && countdownTimer > 0) {
      const timer = setTimeout(() => {
        setCountdownTimer(prev => Math.max(0, prev - 1000));
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (sessionWarning && countdownTimer <= 0) {
      // Auto-logout when countdown reaches zero
      handleSessionLogout({ detail: { reason: 'session_timeout' } });
    }
  }, [sessionWarning, countdownTimer, handleSessionLogout]);

  // Set up event listeners
  useEffect(() => {
    // Secure auth events
    secureAuth.addEventListener('session_warning', handleSessionWarning);
    secureAuth.addEventListener('logout', handleSessionLogout);
    secureAuth.addEventListener('session_restored', handleSessionRestored);
    
    // Browser events
    window.addEventListener('auth_failure', handleAuthFailure);
    
    return () => {
      secureAuth.removeEventListener('session_warning', handleSessionWarning);
      secureAuth.removeEventListener('logout', handleSessionLogout);
      secureAuth.removeEventListener('session_restored', handleSessionRestored);
      window.removeEventListener('auth_failure', handleAuthFailure);
    };
  }, [handleSessionWarning, handleSessionLogout, handleAuthFailure, handleSessionRestored]);

  // Session Warning Modal
  const sessionWarningModal = sessionWarning && (
    <Modal
      open={true}
      onClose={handleExtendSession}
      title="Session Expiring Soon"
      primaryAction={{
        content: 'Extend Session',
        onAction: handleExtendSession,
        variant: 'primary',
      }}
      secondaryActions={[
        {
          content: 'Logout',
          onAction: handleForceLogout,
          destructive: true,
        },
      ]}
      size="small"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <InlineStack gap="300" align="start" blockAlign="center">
            <Icon source={ClockIcon} tone="warning" />
            <BlockStack gap="200">
              <Text variant="bodyMd">
                Your session will expire in{' '}
                <Text as="span" fontWeight="bold" tone="critical">
                  {formatTimeRemaining(countdownTimer)}
                </Text>
              </Text>
              <Text variant="bodySm" tone="subdued">
                Click "Extend Session" to continue working or you'll be automatically logged out.
              </Text>
            </BlockStack>
          </InlineStack>

          <Box paddingBlockStart="200">
            <ProgressBar 
              progress={Math.max(0, (countdownTimer / (sessionWarning.timeLeft || 1)) * 100)}
              size="small"
              tone={countdownTimer < 30000 ? 'critical' : 'warning'}
            />
          </Box>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );

  // Session Expired Modal
  const sessionExpiredModal = isSessionExpired && (
    <Modal
      open={true}
      onClose={() => {}}
      title="Session Expired"
      primaryAction={{
        content: 'Login Again',
        onAction: () => handleForceLogout(),
        variant: 'primary',
      }}
      size="small"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <InlineStack gap="300" align="start" blockAlign="center">
            <Icon source={ExitIcon} tone="critical" />
            <BlockStack gap="200">
              <Text variant="bodyMd">
                Your session has expired for security reasons.
              </Text>
              <Text variant="bodySm" tone="subdued">
                Please log in again to continue using the admin dashboard.
              </Text>
            </BlockStack>
          </InlineStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );

  // Authentication Failure Banner
  const authFailureBanner = authFailure && (
    <Banner
      title="Authentication Failed"
      tone="critical"
      onDismiss={() => setAuthFailure(null)}
    >
      <Text variant="bodySm">
        {authFailure.reason === 'token_expired' 
          ? 'Your login session has expired. Please log in again.' 
          : 'Authentication failed. You will be logged out shortly.'}
      </Text>
    </Banner>
  );

  return (
    <>
      {authFailureBanner}
      {sessionWarningModal}
      {sessionExpiredModal}
    </>
  );
}